import { useState } from "react";
import Select from "./Select";
import classNames from "classnames";

interface FiltersModalDocumentiProps {
  values: {
    agenti: { value: string; label: string } | null;
    codice_cliente: { value: string; label: string } | null;
    tipo_documento: { value: string; label: string } | null;
    selected_date?: string;
    data_da?: string;
    data_a?: string;
    campo_ordinamento?: string;
    tipo_ordinamento?: 'ASC' | 'DESC';
  };
  setFieldValue: (field: string, value: any) => void;
  saveFilters: (filters: {
    selected_date?: string;
    data_da?: string;
    data_a?: string;
    campo_ordinamento: string;
    tipo_ordinamento: 'ASC' | 'DESC';
    tipo_documento?: { value: string; label: string } | null;
  }) => void;
}

const FiltersModalDocumenti: React.FC<FiltersModalDocumentiProps> = ({
  values,
  setFieldValue,
  saveFilters,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dataDa, setDataDa] = useState(values.data_da || "");
  const [dataA, setDataA] = useState(values.data_a || "");
  const [campoOrdinamento, setCampoOrdinamento] = useState(values.campo_ordinamento || "data_calcolata");
  const [tipoOrdinamento, setTipoOrdinamento] = useState<'ASC' | 'DESC'>(values.tipo_ordinamento || "DESC");

  const orderByOptions = [
    { value: "data_calcolata", label: "Data" },
  ];

  const activeCount = [dataDa, dataA].filter(Boolean).length;

  return (
    <div>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="min-h-c py-2 px-4 bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200 font-semibold hover:bg-gray-200 dark:hover:bg-gray-600"
      >
        <span className="mr-2">Filtri Avanzati</span>
        {activeCount > 0 && (
          <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-600 rounded-full">
            {activeCount}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-50"
            onClick={() => setIsOpen(false)}
          />
          <div className="fixed inset-x-0 top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] w-full max-w-lg bg-white dark:bg-neutral-800 rounded-lg shadow-xl z-50 p-6">
            <h2 className="text-xl font-semibold mb-4">Filtri Avanzati</h2>

            {/* Date range */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  Data da
                </label>
                <input
                  type="date"
                  value={dataDa}
                  onChange={(e) => setDataDa(e.target.value)}
                  className="w-full border border-neutral-300 dark:border-neutral-500 rounded px-2 py-1.5 bg-transparent text-gray-700 dark:text-gray-200 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  Data al
                </label>
                <input
                  type="date"
                  value={dataA}
                  min={dataDa || undefined}
                  onChange={(e) => setDataA(e.target.value)}
                  className="w-full border border-neutral-300 dark:border-neutral-500 rounded px-2 py-1.5 bg-transparent text-gray-700 dark:text-gray-200 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Ordinamento */}
            <div className="mt-5">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                Ordinamento
              </label>
              <Select
                value={orderByOptions.find((opt) => opt.value === campoOrdinamento) || null}
                onChange={(option) => setCampoOrdinamento(option?.value || "data_calcolata")}
                options={orderByOptions}
                placeholder="Seleziona un criterio"
                isClearable={false}
              />
              <div className="flex gap-2 mt-2">
                <span
                  onClick={() => setTipoOrdinamento("DESC")}
                  className={classNames("material-symbols-outlined cursor-pointer text-xl", {
                    "text-blue-500 dark:text-blue-400": tipoOrdinamento === "DESC",
                    "text-neutral-300 dark:text-neutral-600": tipoOrdinamento !== "DESC",
                  })}
                  title="Decrescente"
                >
                  arrow_upward
                </span>
                <span
                  onClick={() => setTipoOrdinamento("ASC")}
                  className={classNames("material-symbols-outlined cursor-pointer text-xl", {
                    "text-blue-500 dark:text-blue-400": tipoOrdinamento === "ASC",
                    "text-neutral-300 dark:text-neutral-600": tipoOrdinamento !== "ASC",
                  })}
                  title="Crescente"
                >
                  arrow_downward
                </span>
              </div>
            </div>

            {/* Pulsanti */}
            <div className="flex justify-end gap-4 mt-6 pt-6 border-t dark:border-neutral-600">
              <button
                type="button"
                onClick={() => {
                  setDataDa("");
                  setDataA("");
                  setCampoOrdinamento("data_calcolata");
                  setTipoOrdinamento("DESC");
                  setFieldValue("data_da", "");
                  setFieldValue("data_a", "");
                  setFieldValue("campo_ordinamento", "data_calcolata");
                  setFieldValue("tipo_ordinamento", "DESC");
                  saveFilters({ campo_ordinamento: "data_calcolata", tipo_ordinamento: "DESC" });
                  setIsOpen(false);
                }}
                className="px-4 py-2 text-sm border border-red-600 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md"
              >
                Rimuovi Filtri
              </button>
              <button
                type="button"
                onClick={() => {
                  setFieldValue("data_da", dataDa);
                  setFieldValue("data_a", dataA);
                  setFieldValue("campo_ordinamento", campoOrdinamento);
                  setFieldValue("tipo_ordinamento", tipoOrdinamento);
                  setIsOpen(false);
                }}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Applica Filtri
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default FiltersModalDocumenti;
